import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AccountCircle, Archive, Chat, FilterList, CheckCircle, RadioButtonChecked, RadioButtonUnchecked, Image as ImageIcon, Description as DescriptionIcon, Audiotrack as AudioIcon } from '@material-ui/icons';
import { formatMessageTime } from '../../utils/dateFormatters';
import { ChatListHeader } from '../ChatListHeader';
import {
	ChatList as ChatListContainer,
	ChatListItem,
	ChatListAvatar,
	ChatListInfo,
	ChatListName,
	ChatListMessage,
	LoadMoreButton,
	StatusButton,
	FilterContainer,
	FilterButton,
	ChatContent
} from './styles';
import { useMessage } from 'newComponents/ChatWhatsapp/context/MessageContext';
import { LoadingMessageContainer, MessageTimestamp } from '../ChatArea/styles';
import { useSelector } from 'react-redux';
import { useNotifyAlert } from 'context/NotifyAlert';
import EmojiEmotionsIcon from '@material-ui/icons/EmojiEmotions';

const ChatList = ( {
	conversations,
	searchQuery,
	showUnreadOnly,
	setShowUnreadOnly,
	setSearchQuery,
	handleChatClick,
	activeTab
} ) => {
	const userEmail = useSelector( state => state.auth.userLogin );
	const { loadChats, chatListCursor, updateChat } = useMessage();
	const { setShowNotify } = useNotifyAlert();
	const chatListRef = useRef( null );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ showEndedConversations, setShowEndedConversations ] = useState( false );
	const [ activeFilter, setActiveFilter ] = useState( 'all' );

	// Separa as conversas em ativas e finalizadas
	const { activeConversations, endedConversations } = React.useMemo( () => {
		if ( !conversations ) return { activeConversations: [], endedConversations: [] };
		
		const active = [];
		const ended = [];
		
		conversations.forEach( conversation => {
			if ( conversation?.closedAt ) {
				ended.push( conversation );
			} else {
				active.push( conversation );
			}
		} );
		
		// Ordena as conversas da mais recente para a mais antiga
		const sortByDate = ( a, b ) => {
			const dateA = new Date( a?.createdAt || 0 );
			const dateB = new Date( b?.createdAt || 0 );
			return dateB - dateA;
		};
		
		active.sort( sortByDate );
		ended.sort( sortByDate );
		
		return { activeConversations: active, endedConversations: ended };
	}, [ conversations ] );

	// Filtra as conversas com base no status e na busca
	const filterConversations = ( conversations ) => {
		return conversations?.filter( conversation => {
			const contactName = conversation?.name;
			const contactPhoneNumber = conversation?.id;
			const contactCaseId = conversation?.caseId;
			const processNumber = conversation?.processNumber;
			const content = conversation?.content;
			const matchesSearch = `${contactName} || ${contactPhoneNumber} || ${contactCaseId} || ${processNumber} || ${content}`
				.toLowerCase()
				.includes( searchQuery.toLowerCase() );

			const matchesFilter = activeFilter === 'all' 
				? true 
				: activeFilter === 'open' 
					? !conversation?.status 
					: conversation?.status === activeFilter;

			if ( showUnreadOnly ) {
				return matchesSearch && conversation?.unreadCount > 0 && matchesFilter;
			}
			return matchesSearch && matchesFilter;
		} );
	};

	const filteredActiveConversations = filterConversations( activeConversations );
	const filteredEndedConversations = filterConversations( endedConversations );

	const handleLoadChatList = async () => {
		if ( !chatListRef.current ) return;
		setIsLoading( true );

		const chatDiv = chatListRef.current;

		await loadChats();

		setIsLoading( false );

		const isAtBottom =
      chatDiv.scrollHeight - chatDiv.clientHeight <= chatDiv.scrollTop + 10;

		if ( isAtBottom ) {
			chatDiv.scrollTop = chatDiv.scrollHeight;
		}
	};

	const handleFilterChange = ( filter ) => {
		if ( filter === 'Finalizado' ) {
			setShowEndedConversations( true );
			setActiveFilter( 'all' );
		} else if ( filter === 'all' ) {
			setShowEndedConversations( false );
			setActiveFilter( 'all' );
		} else {
			setActiveFilter( filter );
			setShowEndedConversations( false );
		}
	};

	// Handler para marcar uma conversa como pendente
	const handleMarkAsPending = async ( conversation ) => {
		// Verifica se o usuário atual é o responsável pelo chat
		if ( conversation?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode marcá-lo como pendente.'
			} );
			return;
		}

		try {
			await updateChat( { ...conversation, status: 'Pendente' } );
		} catch ( error ) {
			console.error( 'Erro ao marcar conversa como pendente:', error );
		}
	};

	// Handler para marcar uma conversa como em aberto
	const handleMarkAsOpen = async ( conversation ) => {
		// Verifica se o usuário atual é o responsável pelo chat
		if ( conversation?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode marcá-lo como em aberto.'
			} );
			return;
		}

		try {
			await updateChat( { ...conversation, status: 'Em aberto' } );
		} catch ( error ) {
			console.error( 'Erro ao marcar conversa como em aberto:', error );
		}
	};

	// Handler para alternar o status da conversa
	const handleStatusChange = ( conversation ) => {
		// Verifica se o usuário atual é o responsável pelo chat
		if ( conversation?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode alterar seu status.'
			} );
			return;
		}

		if ( conversation?.status === 'Pendente' ) {
			handleMarkAsOpen( conversation );
		} else {
			handleMarkAsPending( conversation );
		}
	};

	const renderMessageContent = ( conversation ) => {
		if ( !conversation?.content && conversation?.mediaType === 'text' ) return '';

		if ( conversation?.mediaType === 'image' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<ImageIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					<span>Imagem</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'document' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<DescriptionIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					<span>Documento</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'audio' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<AudioIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					<span>Áudio</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'sticker' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<EmojiEmotionsIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					{conversation?.storageUrl && (
						<img src={conversation.storageUrl} alt="Sticker" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', background: 'transparent' }} />
					)}
					<span>Figurinha</span>
				</div>
			);
		}

		// Para mensagens de texto
		return conversation?.content?.length > 30
			? `${conversation?.content?.substring( 0, 30 )}...`
			: conversation?.content;
	};

	// Componente para renderizar um item de conversa
	const renderConversationItem = ( conversation ) => (
		<ChatListItem
			key={conversation.id}
			onClick={() => handleChatClick( conversation )}
			style={{ cursor: 'pointer' }}
			data-ended={conversation?.status?.toLowerCase() === 'finalizado'}
			data-pending={conversation?.status === 'Pendente'}
		>
			<ChatListAvatar>
				{conversation?.contact?.profilePicUrl ? (
					<img
						width={48}
						height={48}
						src={conversation?.profilePicUrl}
						alt={conversation?.name || conversation?.id}
						style={{
							borderRadius : '50%',
							objectFit    : 'cover'
						}}
					/>
				) : (
					<AccountCircle style={{ width: 48, height: 48, color: '#666' }} />
				)}
			</ChatListAvatar>
			<ChatListInfo>
				<ChatContent className="chat-content">
					<div style={{
						display        : 'flex',
						justifyContent : 'space-between',
						width          : '100%',
						marginBottom   : '4px'
					}}>
						<ChatListName>
							{conversation?.name || conversation?.id.split( '@' )[0]}
						</ChatListName>
						<span style={{
							fontSize : '12px',
							color    : '#00a884'
						}}>
							{formatMessageTime( conversation?.createdAt )}
						</span>
					</div>
					<div style={{
						display        : 'flex',
						justifyContent : 'space-between',
						width          : '100%'
					}}>
						<ChatListMessage>
							{renderMessageContent( conversation )}
						</ChatListMessage>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							{conversation?.unreadCount > 0 && (
								<span style={{
									backgroundColor : '#00a884',
									color           : 'white',
									borderRadius    : '12px',
									padding         : '1px 6px',
									fontSize        : '12px',
									textAlign       : 'center'
								}}>
									{conversation?.unreadCount}
								</span>
							)}
						</div>
					</div>
				</ChatContent>
			</ChatListInfo>
			{!conversation?.closedAt && (
				<StatusButton 
					className="status-button"
					status={conversation?.status}
					onClick={( e ) => {
						e.stopPropagation();
						handleStatusChange( conversation );
					}}
					title={conversation?.status === 'Pendente' ? 'Remover pendência' : 'Marcar como pendente'}
				>
					{conversation?.status === 'Pendente' ? (
						<RadioButtonChecked style={{ 
							transition: 'transform 0.2s ease'
						}} />
					) : (
						<RadioButtonUnchecked style={{ 
							transition: 'transform 0.2s ease'
						}} />
					)}
				</StatusButton>
			)}
		</ChatListItem>
	);

	return (
		<ChatListContainer ref={chatListRef}>
			<ChatListHeader
				showUnreadOnly={showUnreadOnly}
				setShowUnreadOnly={setShowUnreadOnly}
				searchQuery={searchQuery}
				setSearchQuery={setSearchQuery}
			/>

			{activeTab !== 'pending' && (
				<FilterContainer>
					<FilterButton 
						active={activeFilter === 'all' && !showEndedConversations}
						onClick={() => handleFilterChange( 'all' )}
						title="Todas as conversas"
					>
						<FilterList />
						<span className="filter-text">Todas</span>
					</FilterButton>
					<FilterButton 
						active={activeFilter === 'Em aberto' && !showEndedConversations}
						onClick={() => handleFilterChange( 'Em aberto' )}
						title="Conversas em aberto"
					>
						<Chat />
						<span className="filter-text">Em Aberto</span>
					</FilterButton>
					<FilterButton 
						active={activeFilter === 'Pendente' && !showEndedConversations}
						onClick={() => handleFilterChange( 'Pendente' )}
						title="Conversas pendentes"
					>
						<CheckCircle />
						<span className="filter-text">Pendentes</span>
					</FilterButton>
					<FilterButton 
						active={showEndedConversations}
						onClick={() => handleFilterChange( 'Finalizado' )}
						title="Conversas finalizadas"
					>
						<Archive />
						<span className="filter-text">Finalizadas</span>
					</FilterButton>
				</FilterContainer>
			)}

			{showEndedConversations ? (
				<div style={{ width: '100%' }}>
					{filteredEndedConversations?.map( renderConversationItem )}
					{filteredEndedConversations?.length === 0 && (
						<div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
							Nenhuma conversa finalizada
						</div>
					)}
				</div>
			) : (
				<div style={{ width: '100%' }}>
					{filteredActiveConversations?.map( renderConversationItem )}
					{filteredActiveConversations?.length === 0 && (
						<div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
							Nenhuma conversa ativa
						</div>
					)}
				</div>
			)}

			{!isLoading && chatListCursor && (
				<LoadMoreButton type='button' onClick={handleLoadChatList}>
					Carregar Mais
				</LoadMoreButton>
			)}
			{isLoading && (
				<LoadingMessageContainer>
					<MessageTimestamp>Carregando mensagens...</MessageTimestamp>
				</LoadingMessageContainer>
			)}
		</ChatListContainer>
	);
};

ChatList.propTypes = {
	conversations: PropTypes.arrayOf(
		PropTypes.shape( {
			id          : PropTypes.string.isRequired,
			name        : PropTypes.string,
			content     : PropTypes.string,
			createdAt   : PropTypes.oneOfType( [ PropTypes.string, PropTypes.number, PropTypes.instanceOf( Date ) ] ),
			unreadCount : PropTypes.number,
			contact     : PropTypes.shape( {
				profilePicUrl: PropTypes.string
			} ),
			profilePicUrl : PropTypes.string,
			closedAt      : PropTypes.oneOfType( [ PropTypes.string, PropTypes.number, PropTypes.instanceOf( Date ) ] ),
			status        : PropTypes.string
		} )
	),
	searchQuery       : PropTypes.string.isRequired,
	showUnreadOnly    : PropTypes.bool.isRequired,
	setShowUnreadOnly : PropTypes.func.isRequired,
	setSearchQuery    : PropTypes.func.isRequired,
	handleChatClick   : PropTypes.func.isRequired,
	activeTab         : PropTypes.string
};

export { ChatList };
